import {
  Group,
  Text,
  Menu,
  UnstyledButton,
  Avatar,
  useMantineColorScheme,
  SegmentedControl,
  Center,
  Box,
  Tooltip,
  Badge,
  ActionIcon,
  Skeleton,
} from '@mantine/core'
import {
  IconChevronDown,
  IconSettings,
  IconLogout,
  IconSun,
  IconMoon,
  IconDeviceDesktop,
  IconLayoutSidebarLeftCollapse,
  IconLayoutSidebarLeftExpand,
  IconApps,
} from '@tabler/icons-react'
import { useAuthStore } from '../../stores/authStore'
import { useNavigate } from 'react-router'
import { NotificationBell } from './NotificationBell'

interface HeaderProps {
  burger?: React.ReactNode
  onToggleSidebar?: () => void
  sidebarCollapsed?: boolean
}

function getEnvironmentBadge(): { label: string; color: string } | null {
  const apiUrl = import.meta.env.VITE_API_URL || ''
  if (!apiUrl) {
    return { label: 'DEV', color: 'green' }
  }
  if (apiUrl.includes('staging')) {
    return { label: 'Staging', color: 'orange' }
  }
  return null // Production - no badge
}

export function Header({ burger, onToggleSidebar, sidebarCollapsed }: HeaderProps) {
  const { user, logout, isInitializing } = useAuthStore()
  const { colorScheme, setColorScheme } = useMantineColorScheme()
  const navigate = useNavigate()
  const envBadge = getEnvironmentBadge()

  const handleLogout = async () => {
    await logout()
    navigate('/', { replace: true })
  }

  const handleSettings = () => {
    navigate('/settings')
  }

  const handleBackToLauncher = () => {
    navigate('/')
  }

  const displayName = user?.profile.displayName || user?.email || 'User'
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)

  return (
    <Group h="100%" px="md" justify="space-between">
      <Group gap="sm">
        {burger}
        <ActionIcon
          variant="subtle"
          onClick={onToggleSidebar}
          visibleFrom="sm"
          aria-label="Toggle sidebar"
        >
          {sidebarCollapsed ? (
            <IconLayoutSidebarLeftExpand size={20} />
          ) : (
            <IconLayoutSidebarLeftCollapse size={20} />
          )}
        </ActionIcon>
        <Text size="lg" fw={600}>
          Accounts - Paper Airplane
        </Text>
        {envBadge && (
          <Badge variant="filled" color={envBadge.color} size="sm">
            {envBadge.label}
          </Badge>
        )}
      </Group>

      <Group gap="xs">
        <Tooltip label="Back to Launcher" position="bottom" withArrow>
          <ActionIcon
            variant="subtle"
            onClick={handleBackToLauncher}
            aria-label="Back to Launcher"
          >
            <IconApps size={20} />
          </ActionIcon>
        </Tooltip>
        <NotificationBell />
        <Menu position="bottom-end" withArrow shadow="md" width={220}>
        <Menu.Target>
          <UnstyledButton>
            <Group gap="xs">
              {isInitializing && !user ? (
                <>
                  <Skeleton height={28} circle />
                  <Box visibleFrom="sm">
                    <Skeleton height={12} width={80} mb={4} />
                    <Skeleton height={10} width={120} />
                  </Box>
                </>
              ) : (
                <>
                  <Avatar size="sm" radius="xl" color="blue">
                    {initials}
                  </Avatar>
                  <Box visibleFrom="sm">
                    <Text size="sm" fw={500} lh={1}>
                      {displayName}
                    </Text>
                    {user?.email && displayName !== user.email && (
                      <Text size="xs" c="dimmed" lh={1.2}>
                        {user.email}
                      </Text>
                    )}
                  </Box>
                </>
              )}
              <IconChevronDown size={14} />
            </Group>
          </UnstyledButton>
        </Menu.Target>

        <Menu.Dropdown>
          <Menu.Label>Account</Menu.Label>
          <Box px="xs" pb="xs">
            <Text size="sm" fw={500}>{displayName}</Text>
            <Text size="xs" c="dimmed">{user?.email}</Text>
          </Box>
          <Menu.Item
            leftSection={<IconApps size={14} />}
            onClick={handleBackToLauncher}
          >
            Back to Launcher
          </Menu.Item>
          <Menu.Item
            leftSection={<IconSettings size={14} />}
            onClick={handleSettings}
          >
            Settings
          </Menu.Item>

          <Menu.Divider />

          <Menu.Label>Appearance</Menu.Label>
          <Box px="xs" pb="xs">
            <SegmentedControl
              fullWidth
              size="xs"
              value={colorScheme}
              onChange={(value) => setColorScheme(value as 'light' | 'dark' | 'auto')}
              data={[
                {
                  value: 'light',
                  label: (
                    <Tooltip label="Light mode" position="bottom" withArrow>
                      <Center>
                        <IconSun size={14} />
                      </Center>
                    </Tooltip>
                  ),
                },
                {
                  value: 'dark',
                  label: (
                    <Tooltip label="Dark mode" position="bottom" withArrow>
                      <Center>
                        <IconMoon size={14} />
                      </Center>
                    </Tooltip>
                  ),
                },
                {
                  value: 'auto',
                  label: (
                    <Tooltip label="System default" position="bottom" withArrow>
                      <Center>
                        <IconDeviceDesktop size={14} />
                      </Center>
                    </Tooltip>
                  ),
                },
              ]}
            />
          </Box>

          <Menu.Divider />

          <Menu.Item
            color="red"
            leftSection={<IconLogout size={14} />}
            onClick={handleLogout}
          >
            Logout
          </Menu.Item>
        </Menu.Dropdown>
      </Menu>
      </Group>
    </Group>
  )
}
